import type { AuthSession, UseSessionStoreType } from '@hourly/contexts/AuthContext';
import { useSyncExternalStore } from 'react';

type ListenerSession = (newSessionData: AuthSession, prevSessionData: AuthSession) => void;

const KEY = '_s_data';

const initialData: AuthSession = {
  userId: null,
  partnerId: null,
  firstName: null,
  lastName: null,
  profilePhoto: null,
  isInOnboarding: false,
};

class SessionStore {
  private sessionData: AuthSession;
  private listeners: Set<ListenerSession> = new Set();

  constructor() {
    const rawData = localStorage.getItem(KEY);

    try {
      this.sessionData = JSON.parse(`${rawData}`) ?? initialData;
    } catch {
      this.sessionData = initialData;
    }
  }

  subscribe = (callback: () => void) => {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  };

  getSnapshot = () => {
    return this.sessionData;
  };

  updateSession = (newSession: Partial<AuthSession>) => {
    const previousData = this.sessionData;
    this.sessionData = { ...this.sessionData, ...newSession };

    const nextDataToPersist = JSON.stringify(this.sessionData);

    localStorage.setItem(KEY, nextDataToPersist);

    this.listeners.forEach((listener) => listener(this.sessionData, previousData));
  };

  clearSession = () => {
    this.updateSession(initialData);
    localStorage.removeItem(KEY);
  };
}

export const sessionStore = new SessionStore();

export function useSessionStore(): UseSessionStoreType {
  const session = useSyncExternalStore(sessionStore.subscribe, sessionStore.getSnapshot);
  return [session, sessionStore.updateSession, sessionStore.clearSession];
}
